import React, { useState } from 'react'
import { ClipboardList, FilePen, LineChart, Megaphone, Newspaper, Wallet } from 'lucide-react'
import { analytics } from '../analytics/analytics'
import { Button } from '../ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { useNavigate } from 'react-router'
import { APP_ROUTES } from '@/common/constants'
import { NAVIGATION_COPY } from '@/locales/en'
import TodoItem from './ToDoItem'
import ProgressRing from './ProgressRing'
import usePersistentProgressAnimation from '@/common/hooks/usePersistentProgressAnimation'
import { useListHoldings } from '@/trading/tradingQueries'
import { useCampaignsWithHoldings } from '@/dashboard/useCampaignsWithHoldings'
import { useFetchKYC } from '@/kyc/kycQueries'
import { useFetchBalance } from '@/wallet/walletQueries'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'

const ToDoDropdown: React.FC = () => {
    const toDoIds = useUserStore((s) => s.user?.toDoIds)
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const { data: walletData, isLoading: isLoadingWallet } = useFetchBalance(activeAccountId)
    const { data: kycData, isLoading: isLoadingKyc } = useFetchKYC(activeAccountId)
    const { data: holdingsData, isLoading: isLoadingHoldings } = useListHoldings(activeAccountId)
    const { campaignsWithHoldings, isLoading: isLoadingCampaigns } = useCampaignsWithHoldings()
    const navigate = useNavigate()
    const [open, setOpen] = useState(false)

    const isLoading = isLoadingHoldings || isLoadingCampaigns || isLoadingKyc || isLoadingWallet
    const hasNoKyc =
        !kycData ||
        !kycData.status ||
        !['VERIFICATION_SUCCESS', 'VERIFICATION_FAILED', 'UNDER_VERIFICATION'].includes(kycData.status)
    const hasNoInvestments = !holdingsData || holdingsData?.holdings.length === 0
    const hasNoCampaigns = campaignsWithHoldings.length === 0
    const hasNoWallet = !walletData || !walletData.totalBalance || walletData.totalBalance === 0
    const hasViewGuide = Array.isArray(toDoIds) && toDoIds.includes('gettingStartedGuide')

    const trackOpenChange = (isOpen: boolean) => {
        setOpen(isOpen)
        if (isOpen) {
            analytics.trackWithDefaults('to_do_list_viewed')
        }
    }

    const onToDoSelect = (itemName: string, route: string) => {
        // close the popover first so the navigation happens after UI closes
        setOpen(false)
        analytics.trackWithDefaults('to_do_list_item_selected', { item_name: itemName })
        navigate(route)
    }

    const total = 5
    let completed = 0
    completed += hasNoKyc ? 0 : 1
    completed += hasNoWallet ? 0 : 1
    completed += hasNoInvestments ? 0 : 1
    completed += hasNoCampaigns ? 0 : 1
    completed += hasViewGuide ? 1 : 0
    const safeTotal = total > 0 ? total : 1
    const safeCompleted = Math.max(0, Math.min(completed, safeTotal))

    const radius = 16

    const computedTargetPct = safeTotal > 0 ? Math.round((safeCompleted / safeTotal) * 100) : 0

    const { displayPct: ringPct, animateBg } = usePersistentProgressAnimation(computedTargetPct, isLoading)

    return (
        <Popover open={open} onOpenChange={trackOpenChange}>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    className="w-10 h-10 relative rounded-[100px] border-0"
                    aria-label="Open to-do list"
                    aria-haspopup="menu"
                >
                    <ProgressRing
                        size={40}
                        radius={radius}
                        pct={ringPct}
                        strokeWidth={3.5}
                        className="absolute inset-0 m-auto z-20"
                    />
                    <div
                        className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full z-10 flex items-center justify-center transition-colors duration-300 ease-out ${
                            animateBg ? '' : 'bg-[#E4FBC6]'
                        }`}
                    >
                        <ClipboardList className="z-30" aria-hidden />
                    </div>
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-full md:w-96 max-w-[calc(100vw)] p-2 rounded-[10px] inline-flex flex-col justify-start items-start gap-2"
                align="end"
                mobileAnimate={true}
            >
                <div className="self-stretch px-2 inline-flex justify-between items-center">
                    <div className="flex-1 inline-flex flex-col justify-center items-start">
                        <div className="inline-flex justify-start items-center gap-2">
                            <div className="text-center justify-start text-black text-3xl font-normal font-['Serious Sans'] leading-10">
                                {NAVIGATION_COPY.toDo.title}
                            </div>
                        </div>
                        <div className="self-stretch justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                            {NAVIGATION_COPY.toDo.description}
                        </div>
                    </div>
                    <div className="w-14 h-14 flex justify-start items-center gap-2.5">
                        <div className="relative w-14 h-14">
                            <ProgressRing
                                size={56}
                                radius={radius + 6}
                                pct={ringPct}
                                strokeWidth={4}
                                className="absolute inset-0 m-auto z-10"
                            />

                            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full z-20 flex items-center justify-center">
                                <div className="text-stone-700 text-base font-semibold font-['Inter'] leading-6">
                                    {safeCompleted}/{safeTotal}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-brand-dust"></div>

                <div
                    role="list"
                    aria-label={NAVIGATION_COPY.toDo.title}
                    className="self-stretch flex flex-col w-full gap-2"
                >
                    <TodoItem
                        icon={<FilePen size={16} className="text-stone-700" />}
                        number={NAVIGATION_COPY.toDo.items[0].number}
                        title={NAVIGATION_COPY.toDo.items[0].title}
                        description={NAVIGATION_COPY.toDo.items[0].description}
                        completed={!hasNoKyc}
                        onClick={
                            hasNoKyc
                                ? () =>
                                      onToDoSelect(
                                          `${NAVIGATION_COPY.toDo.items[0].number}. ${NAVIGATION_COPY.toDo.items[0].title}`,
                                          APP_ROUTES.APPLICATION_RESUME,
                                      )
                                : undefined
                        }
                    />
                    <TodoItem
                        icon={<Wallet size={16} className="text-stone-700" />}
                        number={NAVIGATION_COPY.toDo.items[1].number}
                        title={NAVIGATION_COPY.toDo.items[1].title}
                        description={NAVIGATION_COPY.toDo.items[1].description}
                        completed={!hasNoWallet}
                        onClick={() =>
                            onToDoSelect(
                                `${NAVIGATION_COPY.toDo.items[1].number}. ${NAVIGATION_COPY.toDo.items[1].title}`,
                                APP_ROUTES.WALLET,
                            )
                        }
                    />
                    <TodoItem
                        icon={<Newspaper size={16} className="text-stone-700" />}
                        number={NAVIGATION_COPY.toDo.items[2].number}
                        title={NAVIGATION_COPY.toDo.items[2].title}
                        description={NAVIGATION_COPY.toDo.items[2].description}
                        completed={hasViewGuide}
                        onClick={() =>
                            onToDoSelect(
                                `${NAVIGATION_COPY.toDo.items[2].number}. ${NAVIGATION_COPY.toDo.items[2].title}`,
                                '/getting-started',
                            )
                        }
                    />
                    <TodoItem
                        icon={<LineChart size={16} className="text-stone-700" />}
                        number={NAVIGATION_COPY.toDo.items[3].number}
                        title={NAVIGATION_COPY.toDo.items[3].title}
                        description={NAVIGATION_COPY.toDo.items[3].description}
                        completed={!hasNoInvestments}
                        onClick={() =>
                            onToDoSelect(
                                `${NAVIGATION_COPY.toDo.items[3].number}. ${NAVIGATION_COPY.toDo.items[3].title}`,
                                APP_ROUTES.INVEST,
                            )
                        }
                    />
                    <TodoItem
                        icon={<Megaphone size={16} className="text-stone-700" />}
                        number={NAVIGATION_COPY.toDo.items[4].number}
                        title={NAVIGATION_COPY.toDo.items[4].title}
                        description={NAVIGATION_COPY.toDo.items[4].description}
                        completed={!hasNoCampaigns}
                        onClick={() =>
                            onToDoSelect(
                                `${NAVIGATION_COPY.toDo.items[4].number}. ${NAVIGATION_COPY.toDo.items[4].title}`,
                                APP_ROUTES.CAMPAIGNS,
                            )
                        }
                    />
                </div>
            </PopoverContent>
        </Popover>
    )
}

export default React.memo(ToDoDropdown)
